import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import LayoutEvent from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutEvent";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import ScreenNavigator from "../../../../../lib/html5-software-library/src/ranj/ui/ScreenNavigator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import SmartphoneNavigateEvent from "../model/SmartphoneNavigateEvent";
import SmartphoneSetupNavigatorEvent from "../model/SmartphoneSetupNavigatorEvent";
import SmartphoneSkinKeyRegistry from "../model/SmartphoneSkinKeyRegistry";
import AbstractSmartphoneMediator from "./AbstractSmartphoneMediator";
import SmartphoneSoundType from "./SmartphoneSoundType";

export default class SmartphoneMediator extends AbstractSmartphoneMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {SoundManager} soundManager 
     * @param {ScreenNavigator} screenNavigator 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, soundManager, screenNavigator) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._soundManager = soundManager;
        this._screenNavigator = screenNavigator;
    }

    initialize() {
        super.initialize();

        this._setupSkin();

        this._hideSmartphone(false);

        this.registerEventListener(SmartphoneNavigateEvent.SMARTPHONE_SHOW_REQUEST, this._onSmartphoneShowRequest.bind(this));
        this.registerEventListener(SmartphoneNavigateEvent.SMARTPHONE_HIDE_REQUEST, this._onSmartphoneHideRequest.bind(this));

        this.registerEventListener(LayoutEvent.CHANGED, this._onLayoutChanged.bind(this));
    }

    destroy() {
        createjs.Tween.removeTweens(this._deviceSkin.closeButton);

        this._deviceSkin = null;

        this._overlay.removeEventListener('click', this._onOverlayClickedBound);

        super.destroy();
    }

    _setupSkin() {
        // Overlay to prevent click through
        this._overlay = new lib.SmartphoneAssets.SmartphoneOverlay();
        this._target.addChild(this._overlay);
        this._onOverlayClickedBound = this._onOverlayClicked.bind(this);
        this._overlay.addEventListener('click', this._onOverlayClickedBound);
        this._overlay.alpha = 0;

        this.stretchBackground(this._overlay);

        // Device skin
        this._deviceSkin = new lib.SmartphoneAssets.SmartphoneDeviceSkin();
        this._target.addChild(this._deviceSkin);
        this._deviceSkin.initialX = this._deviceSkin.x;
        this._deviceSkin.initialY = this._deviceSkin.y;

        this._deviceSkin.closeButton.gotoAndStop(0);
        this._deviceSkin.closeButton.snapToPixel = false;
        this._deviceSkin.closeButton.initialY = this._deviceSkin.closeButton.y;
        createjs.Tween.get(this._deviceSkin.closeButton, { loop: -1 })
            .to({ y: this._deviceSkin.closeButton.initialY + 5 }, 700)
            .to({ y: this._deviceSkin.closeButton.initialY }, 300);

        // Hand holding the smartphone
        if (this._deviceSkin.hand) {
            this._deviceSkin.hand.visible = !!SmartphoneMediator.SHOW_HAND_HOLDING_PHONE;
            this._deviceSkin.hand.mouseEnabled = false;
        }

        // Determine viewport based on screen canvas (defined in the FLA)
        const screenCanvas = this._deviceSkin.screenCanvas;
        this._screenViewport = new createjs.Rectangle(screenCanvas.x, screenCanvas.y, screenCanvas.nominalBounds.width, screenCanvas.nominalBounds.height);

        screenCanvas.addEventListener('click', () => { });

        const navigatorContextView = new createjs.Container();
        navigatorContextView.x = this._screenViewport.x;
        navigatorContextView.y = this._screenViewport.y;
        this._deviceSkin.addChild(navigatorContextView);

        // Configure and 'attach' the smartphone navigator to this mediator
        const setupNavigatorEvent = new SmartphoneSetupNavigatorEvent(SmartphoneSetupNavigatorEvent.PERFORM, navigatorContextView, new createjs.Point(this._screenViewport.width, this._screenViewport.height))
        this.dispatch(setupNavigatorEvent.type, { data: setupNavigatorEvent });

        this.gotoScreen(SmartphoneSkinKeyRegistry.SMARTPHONE_CHAT_CONTACTS_SCREEN_SKIN);
    }

    _onOverlayClicked() {
        const navEvt = new SmartphoneNavigateEvent(SmartphoneNavigateEvent.CLOSE_ALL_SCREENS_REQUEST);
        this.dispatch(navEvt.type, { data: navEvt });

        this.gotoScreen(SmartphoneSkinKeyRegistry.SMARTPHONE_CHAT_CONTACTS_SCREEN_SKIN);

        this.dispatch(SmartphoneNavigateEvent.SMARTPHONE_HIDE_REQUEST);
    }

    _onLayoutChanged() {
        if (!this._isShowing) {
            this._hideSmartphone(false);
        }
    }

    _onSmartphoneShowRequest() {
        this._showSmartphone();
    }

    _onSmartphoneHideRequest() {
        this._hideSmartphone();
    }

    _showSmartphone() {
        this._soundManager.playSound(SmartphoneSoundType.SMARTPHONE_OPEN);
        createjs.Tween.get(this._deviceSkin).to({ x: this._deviceSkin.initialX, y: this._deviceSkin.initialY }, 350, createjs.Ease.sineInOut);

        createjs.Tween.get(this._overlay).to({ alpha: 1 }, 350, createjs.Ease.sineInOut);

        this._isShowing = true;
    }

    _hideSmartphone(animated = true) {
        if (animated) {
            this.disableInteraction();

            this._soundManager.playSound(SmartphoneSoundType.SMARTPHONE_CLOSE);
            createjs.Tween.get(this._deviceSkin).to({
                x: 0,
                y: this._screenNavigator.getScreenHeight(),
            }, 350, createjs.Ease.sineInOut);

            createjs.Tween.get(this._overlay).to({ alpha: 0 }, 350, createjs.Ease.sineInOut)
                .call(() => {
                    this.enableInteraction(); // Be mindful that removing this tween will cause interaction to not be re-enabled.
                });
        } else {
            this._deviceSkin.x = 0;
            this._deviceSkin.y = this._screenNavigator.getScreenHeight();
            this._overlay.alpha = 0;
        }

        this._isShowing = false;
    }
}

SmartphoneMediator.SHOW_HAND_HOLDING_PHONE = true;